import { auth, firestore, database, storage } from '@breakroom-nx/apis';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  setDoc,
  GeoPoint,
  limitToLast,
  deleteField,
  deleteDoc,
} from 'firebase/firestore';

import {
  ref as refDB,
  set,
  onValue,
  query as dbQuery,
  orderByChild,
  push,
  get,
  limitToFirst,
  limitToLast as limitToLastDB,
  equalTo,
  update,
} from 'firebase/database';

import {
  User,
  Notification,
  userConverter,
  getUserData,
  notificationConverter,
} from '@breakroom-nx/classes';

import { getAuth } from 'firebase/auth';

import {
  ref,
  getDownloadURL,
  uploadBytes,
  deleteObject,
} from 'firebase/storage';
import { TogglePrivacyOption } from '@breakroom-nx/ui';
import { PermissionStatus } from '@breakroom-nx/helpers';
import { SerializedError } from '@reduxjs/toolkit'; // Ensure SerializedError is imported

const apiName = '[USER API]';
export const userAPI = createApi({
  reducerPath: 'userApi',
  keepUnusedDataFor: 3600,
  baseQuery: fakeBaseQuery<Error>(),
  tagTypes: [
    'Client',
    'Privacy',
    'User',
    'Notification',
    'Friend',
    'AccountStates',
  ],
  endpoints: (builder) => ({
    getCurrentUser: builder.query<User, void>({
      providesTags: ['Client'],
      async queryFn() {
        try {
          // console.info(`${apiName} Getting current user`);
          const auth = getAuth();
          if (!auth.currentUser) throw new Error('No current user found');

          const docRef = doc(
            firestore,
            'users',
            auth.currentUser.uid
          ).withConverter(userConverter);

          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const user = docSnap.data();
            // console.log(`${apiName} Current user: ${user.username}`);
            return { data: user };
          } else {
            throw new Error('User document does not exist');
          }
        } catch (err) {
          // console.log(`${apiName} (ERROR) Unable to get current user: ${err}`);
          throw new Error('Unable to load your user data');
        }
      },
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        let unsubscribe = () => {};
        try {
          await cacheDataLoaded.then(() => {
            // console.log(
            //   `${apiName} Cached Data for the current user has been loaded!`
            // );
          });

          // TODO: Remove this auth call logic
          const auth = getAuth();
          if (!auth.currentUser) throw new Error('No current user found');
          const docRef = doc(
            firestore,
            'users',
            auth.currentUser.uid
          ).withConverter(userConverter);

          // console.log(`${apiName} Performing cache fetch...`);
          unsubscribe = onSnapshot(docRef, (snapshot) => {
            let user: User;
            if (snapshot.exists()) {
              user = snapshot.data();
              // console.log(`${apiName} Current user: ${user.username}`);
            }

            updateCachedData((draft) => {
              Object.assign(draft, user);
            });
          });
        } catch (err) {
          // console.log(
          //   `${apiName} (Error) Unable to update cache for current user`
          // );
        }

        await cacheEntryRemoved;
        unsubscribe && unsubscribe();
      },
    }),

    getCurrentUserPrivacyStates: builder.query<
      {
        showName: boolean;
        showAge: boolean;
        showLocation: boolean;
        showOnlineStatus: boolean;
      },
      void
    >({
      providesTags: ['Privacy'],
      async queryFn() {
        try {
          // console.info(`${apiName}: Getting user's privacy choices...`);
          const auth = getAuth();
          if (!auth.currentUser) throw new Error('No current user found');

          const docRef = doc(
            firestore,
            'users',
            auth.currentUser.uid,
            'private',
            'privacy'
          );

          const docSnap = await getDoc(docRef).catch((err) => {
            throw new Error(err);
          });

          if (docSnap.exists()) {
            const privacySettings = docSnap.data();

            return {
              data: {
                showName: privacySettings.showName,
                showAge: privacySettings.showAge,
                showLocation: privacySettings.showLocation,
                showOnlineStatus: privacySettings.showOnlineStatus,
              },
            };
          } else {
            throw new Error('User privacy document does not exist');
          }
        } catch (err) {
          // console.log(
          //   `${apiName} (Error) Unable to get current user's privacy settings: ${err}`
          // );
          throw new Error('Unable to get your privacy settings');
        }
      },
    }),

    getCurrentUserAccountStates: builder.query<
      {
        completedOnboarding: boolean;
        cookiePermissions: PermissionStatus;
        locationPermissions: PermissionStatus;
        notificationPermissions: PermissionStatus;
      },
      void
    >({
      providesTags: ['AccountStates'],
      async queryFn() {
        try {
          // console.info(`${apiName}: Getting client's account states...`);
          const auth = getAuth();
          if (!auth.currentUser) throw new Error('No current user found');

          const docRef = doc(
            firestore,
            'users',
            auth.currentUser.uid,
            'private',
            'account'
          );

          const docSnap = await getDoc(docRef).catch((err) => {
            throw new Error(err);
          });

          if (docSnap.exists()) {
            const accountStates = docSnap.data();

            return {
              data: {
                completedOnboarding: accountStates.completedOnboarding,
                cookiePermissions:
                  accountStates.cookiePermissions as PermissionStatus,
                locationPermissions:
                  accountStates.locationPermissions as PermissionStatus,
                notificationPermissions:
                  accountStates.notificationPermissions as PermissionStatus,
              },
            };
          } else {
            throw new Error('User private account document does not exist');
          }
        } catch (err) {
          // console.log(
          //   `${apiName} (Error) Unable to get client's account states: ${err}`
          // );
          throw new Error('Unable to get your account states');
        }
      },
    }),

    updateUserAccountStates: builder.mutation<
      null,
      Partial<{
        completedOnboarding: boolean;
        cookiePermissions: PermissionStatus;
        locationPermissions: PermissionStatus;
        notificationPermissions: PermissionStatus;
      }>
    >({
      invalidatesTags: ['AccountStates'],
      async queryFn(patch) {
        try {
          const uid = auth.currentUser?.uid;

          if (!uid) throw new Error('Current user UID cannot be undefined');

          // console.log(`${apiName} Updating the Client's Account States`);

          const docRef = doc(firestore, 'users', uid, 'private', 'account');

          await updateDoc(docRef, patch);

          return { data: null };
        } catch (err) {
          // console.log(
          //   `${apiName} Error Updating Account States for Client: ${err}`
          // );
          throw new Error('Error updating your account states');
        }
      },
    }),

    getCurrentUserLocation: builder.query<
      {
        location: GeoPoint;
        geohash: string;
      },
      null
    >({
      async queryFn() {
        try {
          // console.info(`${apiName}: Getting user's location...`);
          const auth = getAuth();
          if (!auth.currentUser) throw new Error('No current user found');

          const docRef = doc(
            firestore,
            'users',
            auth.currentUser.uid,
            'private',
            'location'
          );

          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const userLocation = docSnap.data();

            return {
              data: {
                location: userLocation.location,
                geohash: userLocation.geohash,
              },
            };
          } else {
            throw new Error('User location document does not exist');
          }
        } catch (err) {
          // console.log(
          //   `${apiName} (Error) Unable to get current user's location: ${err}`
          // );
          throw new Error('Unable to get your current location');
        }
      },
    }),

    // Get User Data
    getUserFromUID: builder.query<User, string | undefined>({
      // providesTags: ['User'],

      async queryFn(uid): Promise<QueryReturnValue<User[], SerializedError>> {
        try {
          // console.info(`${apiName}: Getting user data for ${uid}`);
          if (!uid) throw new Error('Current user UID cannot be undefined');

          const docRef = doc(firestore, 'users', uid).withConverter(
            userConverter
          );

          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const user = docSnap.data();
            return { data: user };
          } else {
            throw new Error('User document does not exist');
          }
        } catch (err) {
          // console.log(`${apiName} (Error) Unable to get user data: ${err}`);
          throw new Error('Unable to retrieve the user');
        }
      },
    }),

    // Get User Data
    getUserBlockState: builder.query<boolean, string | undefined>({
      // providesTags: ['User'],

      async queryFn(uid) {
        try {
          const auth = getAuth();
          // console.info(`${apiName}: Getting user block state for ${uid}`);

          if (!auth.currentUser) throw new Error('No current user found');
          if (!uid) throw new Error('Requested user cannot be undefined');

          const docRef = doc(
            firestore,
            'users',
            auth.currentUser.uid,
            'blocked',
            uid
          );
          const docSnap = await getDoc(docRef).catch((err) => {
            throw new Error('Error getting the blocked document');
          });

          if (docSnap.exists()) {
            // Requested user is blocked
            return { data: true };
          } else {
            return { data: false };
          }
        } catch (err) {
          // console.log(
          //   `${apiName} (Error) Unable to get user block state: ${err}`
          // );
          throw new Error('Unable to retrieve the blocked state');
        }
      },
    }),

    updateBlockState: builder.mutation<null, string>({
      // invalidatesTags: ['Client'],
      async queryFn(patch) {
        try {
          const auth = getAuth();
          // console.info(`${apiName}: Updating user block state for ${patch}`);

          if (!auth.currentUser) throw new Error('No current user found');
          if (!patch) throw new Error('Requested user cannot be undefined');

          const docRef = doc(
            firestore,
            'users',
            auth.currentUser.uid,
            'blocked',
            patch
          );
          const docSnap = await getDoc(docRef).catch((err) => {
            throw new Error('Error getting the blocked document');
          });

          if (docSnap.exists()) {
            // Requested user is blocked -> delete document
            await deleteDoc(docRef);
          } else {
            // Requested user not blocked -> create blocked document
            await setDoc(docRef, {
              isBlocked: true,
            });
          }

          return { data: null };
        } catch (err) {
          // console.log(`${apiName} Error Updating Data for Client: ${err}`);
          throw new Error('Error updating your data');
        }
      },
    }),

    // TODO: Optimize
    // Get User's Notifications
    getUserNotifications: builder.query<Notification[], void>({
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ uid }) => ({
                type: 'Notification' as const,
                uid,
              })),
              'Notification',
            ]
          : ['Notification'],

      async queryFn(arg, api, extraOptions, baseQuery) {
        return {
          data: await new Promise((resolve, reject) => {
            const uid = auth.currentUser?.uid;
            const notificationsRef = refDB(database, 'notifications/' + uid);
            const notificationsQuery = dbQuery(
              notificationsRef,
              orderByChild('createdAt'),
              limitToLastDB(50)
            );

            onValue(notificationsQuery, (snapshot) => {
              let notifications: Notification[] = [];
              snapshot.forEach((doc) => {
                // Use the Notification class constructor for each notification
                const raw = doc.val();
                const notification = new Notification({
                  uid: doc.key || undefined,
                  type: raw.type,
                  title: raw.title,
                  message: raw.message,
                  read: raw.read ?? false,
                  createdAt: raw.createdAt
                    ? new Date(raw.createdAt)
                    : new Date(),
                  updatedAt: raw.updatedAt
                    ? new Date(raw.updatedAt)
                    : undefined,
                  actions: raw.actions ?? [],
                  notificationCreator: raw.notificationCreator,
                  roomBeingInvitedTo: raw.roomBeingInvitedTo,
                });
                notifications.sort(
                  (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
                );
                notifications.push(notification);
              });
              resolve(notifications);
            });
          }),
        };
      },

      async onCacheEntryAdded(
        _arg: void,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        let unsubscribe = () => {};
        try {
          const uid = auth.currentUser?.uid;
          if (!uid) throw new Error('Current user UID cannot be undefined');

          await cacheDataLoaded.then((cache) => {
            // console.log(
            //   `Cache Data Loaded: [Notifications Size: ${cache.data.length}]`
            // );
          });

          const notificationsRef = refDB(database, 'notifications/' + uid);
          const notificationsQuery = dbQuery(
            notificationsRef,
            limitToLastDB(50)
          );

          unsubscribe = onValue(notificationsQuery, (snapshot) => {
            let notifications: Notification[] = [];
            snapshot.forEach((doc) => {
              const raw = doc.val();
              const notification = new Notification({
                uid: doc.key || undefined,
                type: raw.type,
                title: raw.title,
                message: raw.message,
                read: raw.read ?? false,
                createdAt: raw.createdAt ? new Date(raw.createdAt) : new Date(),
                updatedAt: raw.updatedAt ? new Date(raw.updatedAt) : undefined,
                actions: raw.actions ?? [],
                notificationCreator: raw.notificationCreator,
                roomBeingInvitedTo: raw.roomBeingInvitedTo,
              });
              notifications.push(notification);
            });

            updateCachedData((draft) => {
              return notifications;
            });
          });
        } catch (err) {
          // console.log(
          //   `${apiName} (Error) Unable to update user notification cache: ${err}`
          // );

          const errorString = 'Unable to get notifications at this time.';
          throw new Error(errorString);
        }

        await cacheEntryRemoved;
        unsubscribe && unsubscribe();
      },
    }),

    // Get User's Friends
    getFriendsForUserWithUID: builder.query<User[], string>({
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ uid }) => ({
                type: 'Friend' as const,
                uid,
              })),
              'Friend',
            ]
          : ['Friend'],
      async queryFn(uid) {
        try {
          if (!uid) {
            return {
              error: {
                message: 'User ID is undefined',
                name: 'InvalidArgumentError',
              },
            };
          }

          // console.info(`${apiName} Getting friends for user ${uid}`);

          const friendsRef = refDB(database, 'friends/' + uid);

          const snapshot = await get(friendsRef);
          const friendIds: string[] = [];
          snapshot.forEach((doc) => {
            const friendId = doc.key;
            if (friendId) {
              friendIds.push(friendId);
            }
          });

          if (friendIds.length === 0) {
            return {
              error: {
                message: 'No friends found',
                name: 'NotFoundError',
              },
            };
          }

          // Fetch user data for each friend in parallel using Promise.all
          const fetchUserDataPromises = friendIds.map((id) => getUserData(id));

          // Wait for all promises to resolve
          const friendDataArray = await Promise.all(fetchUserDataPromises);

          // Filter out null values (if any)
          const friends: User[] = friendDataArray.filter(
            (userData): userData is User => userData !== null
          );

          return { data: friends };
        } catch (err) {
          // console.error(
          //   `${apiName} (ERROR) Unable to get friends for user: ${err}`
          // );
          return {
            error: {
              message: 'Unable to fetch friends',
              name: 'FetchError',
              stack: err instanceof Error ? err.stack : undefined,
            },
          };
        }
      },
    }),

    //! Mutations
    updateUserData: builder.mutation<null, Partial<User>>({
      invalidatesTags: ['Client'],
      async queryFn(patch) {
        try {
          const uid = auth.currentUser?.uid;

          if (!uid) throw new Error('Current user UID cannot be undefined');

          // 1) Write the Data to the User's Private Document
          const privateDocRef = doc(
            firestore,
            'users',
            uid,
            'private',
            'personal'
          );
          await updateDoc(privateDocRef, patch).catch((err) => {
            throw new Error(err);
          });

          // TODO: Check the User's Privacy Settings

          // 3) Write to the User's Public Document
          // console.log(`${apiName} Updating the Client's Data`);
          const docRef = doc(firestore, 'users', uid).withConverter(
            userConverter
          );

          await updateDoc(docRef, patch);

          return { data: null };
        } catch (err) {
          // console.log(`${apiName} Error Updating Data for Client: ${err}`);
          throw new Error('Error updating your data');
        }
      },
    }),

    updateUserLocation: builder.mutation<null, Partial<User>>({
      invalidatesTags: ['Client'],
      async queryFn(patch) {
        try {
          const uid = auth.currentUser?.uid;

          if (!uid) throw new Error('Current user UID cannot be undefined');

          // console.log(`${apiName} Updating the Client's Location`);

          // Update public path with geocode
          const publicDocPath = doc(firestore, 'users', uid);
          await updateDoc(publicDocPath, {
            geoCode: patch.geoCode?.toLowerCase(),
          }).catch((err) => {
            // console.log(
            //   `${apiName} Failed to set geocode publicly for user. Error: ${err}`
            // );
          });

          // Update private path
          const docRef = doc(firestore, 'users', uid, 'private', 'location');
          await getDoc(docRef).then(async (snapshot) => {
            if (snapshot.exists()) {
              await updateDoc(docRef, patch).catch((err) => {
                throw new Error(
                  `Error updating doc: ${err.errorCode} | ${err.errorMessage}`
                );
              });
            } else {
              await setDoc(docRef, patch).catch((err) => {
                throw new Error(
                  `Error setting doc: ${err.errorCode} | ${err.errorMessage}`
                );
              });
            }
          });

          return { data: null };
        } catch (err) {
          // console.log(`${apiName} Error Updating Location for Client: ${err}`);
          throw new Error('Error updating your location');
        }
      },
    }),

    updatePrivacySettings: builder.mutation<
      null,
      { privacyOption: TogglePrivacyOption; turnOn: boolean }
    >({
      invalidatesTags: ['User', 'Privacy'],
      async queryFn(patch) {
        try {
          const uid = auth.currentUser?.uid;

          if (!uid) throw new Error('Current user UID cannot be undefined');

          // console.log(`${apiName} Updating the Client's Privacy States`);

          // Update public path with geocode
          const userDocPath = doc(firestore, 'users', uid);
          const userPrivacyDocPath = doc(
            firestore,
            'users',
            uid,
            'private',
            'privacy'
          );

          if (patch.turnOn) {
            // Enable Privacy State -> Delete the Private Documents from the User's Document
            switch (patch.privacyOption) {
              case TogglePrivacyOption.Name:
                await updateDoc(userPrivacyDocPath, {
                  showName: !patch.turnOn,
                })
                  .then(async () => {
                    await updateDoc(userDocPath, {
                      name: deleteField(),
                    }).catch((err) => {
                      throw new Error(err);
                    });
                  })
                  .catch((err) => {
                    throw new Error(err);
                  });
                break;
              case TogglePrivacyOption.Age:
                await updateDoc(userPrivacyDocPath, {
                  showAge: !patch.turnOn,
                })
                  .then(async () => {
                    await updateDoc(userDocPath, {
                      dateOfBirth: deleteField(),
                    }).catch((err) => {
                      throw new Error(err);
                    });
                  })
                  .catch((err) => {
                    throw new Error(err);
                  });
                break;
              case TogglePrivacyOption.Location:
                await updateDoc(userPrivacyDocPath, {
                  showLocation: !patch.turnOn,
                })
                  .then(async () => {
                    await updateDoc(userDocPath, {
                      location: deleteField(),
                    }).catch((err) => {
                      throw new Error(err);
                    });
                  })
                  .catch((err) => {
                    throw new Error(err);
                  });
                break;
              case TogglePrivacyOption.OnlineStatus:
                await updateDoc(userPrivacyDocPath, {
                  showOnlineStatus: !patch.turnOn,
                })
                  .then(async () => {
                    await updateDoc(userDocPath, {
                      onlineStatus: deleteField(),
                    }).catch((err) => {
                      throw new Error(err);
                    });
                  })
                  .catch((err) => {
                    throw new Error(err);
                  });
                break;
            }
          } else {
            // Turn off the Privacy State -> Copy over the field from the User's private collection back to their public doc.
            const userPersonalDocPath = doc(
              firestore,
              'users',
              uid,
              'private',
              'personal'
            );
            await getDoc(userPersonalDocPath)
              .then(async (snapshot) => {
                const data = snapshot.data();
                if (data) {
                  switch (patch.privacyOption) {
                    case TogglePrivacyOption.Name:
                      await updateDoc(userPrivacyDocPath, {
                        showName: !patch.turnOn,
                      })
                        .then(async () => {
                          await updateDoc(userDocPath, {
                            name: data.name,
                          }).catch((err) => {
                            throw new Error(err);
                          });
                        })
                        .catch((err) => {
                          throw new Error(err);
                        });
                      break;
                    case TogglePrivacyOption.Age:
                      await updateDoc(userPrivacyDocPath, {
                        showAge: !patch.turnOn,
                      })
                        .then(async () => {
                          await updateDoc(userDocPath, {
                            dateOfBirth: data.dateOfBirth,
                          }).catch((err) => {
                            throw new Error(err);
                          });
                        })
                        .catch((err) => {
                          throw new Error(err);
                        });
                      break;
                    case TogglePrivacyOption.Location:
                      await updateDoc(userPrivacyDocPath, {
                        showLocation: !patch.turnOn,
                      })
                        .then(async () => {
                          await updateDoc(userDocPath, {
                            location: data.location,
                          }).catch((err) => {
                            throw new Error(err);
                          });
                        })
                        .catch((err) => {
                          throw new Error(err);
                        });
                      break;
                    case TogglePrivacyOption.OnlineStatus:
                      await updateDoc(userPrivacyDocPath, {
                        showOnlineStatus: !patch.turnOn,
                      })
                        .then(async () => {
                          await updateDoc(userDocPath, {
                            onlineStatus: data.onlineStatus,
                          }).catch((err) => {
                            throw new Error(err);
                          });
                        })
                        .catch((err) => {
                          throw new Error(err);
                        });
                      break;
                  }
                }
              })
              .catch((err) => {
                throw new Error(
                  `Error getting the user's personal doc. Error: ${err}`
                );
              });
          }

          return { data: null };
        } catch (err) {
          // console.log(
          //   `${apiName} Error Updating Privacy Setting for Client: ${err}`
          // );
          throw new Error(`Error Updating Privacy Setting for Client: ${err}`);
        }
      },
    }),

    uploadUserPhoto: builder.mutation<string, string>({
      // invalidatesTags: ['Client'],
      async queryFn(photoPath) {
        try {
          const uid = auth.currentUser?.uid;

          if (!uid || !photoPath) {
            throw new Error('UID or photo path is empty');
          }

          const path = `users/${uid}/avatar.jpg`;
          // console.log('PHOTO: ', photoPath);
          // console.log('PATH: ', path);

          const photoRef = ref(storage, path);

          // Convert Image to Array of Bytes
          const img = await fetch(photoPath);
          const bytes = await img.blob();

          const URL = await uploadBytes(photoRef, bytes).then(
            async (snapshot) => {
              return await getDownloadURL(snapshot.ref).then((URL) => {
                // console.log('URL: ', URL);
                return URL;
              });
            }
          );

          // Set User's Avatar to the Photo URL
          const docRef = doc(firestore, 'users', uid).withConverter(
            userConverter
          );

          await updateDoc(docRef, { avatar: URL }).catch((err) => {
            // console.log(
            //   `${apiName} (Error) Error uploading avatar url to user's doc: ${err.errorCode} ${err.errorMessage}`
            // );

            throw new Error(err);
          });

          return { data: URL };
        } catch (err) {
          // console.log(`${apiName} (Error) Error uploading image for user`);
          throw new Error('Error updating your photo');
        }
      },
    }),

    deleteUserPhoto: builder.mutation<null, string>({
      invalidatesTags: ['Client'],
      async queryFn(avatarPath) {
        try {
          const uid = auth.currentUser?.uid;

          if (!uid || !avatarPath) {
            throw new Error(
              'Current user UID and replacement avatar cannot be undefined'
            );
          }

          // Update Avatar from User's Profile
          // console.log(`${apiName} Deleting the client's photo`);
          const docRef = doc(firestore, 'users', uid).withConverter(
            userConverter
          );

          await updateDoc(docRef, { avatar: avatarPath });

          // Delete Image from Storage
          const userImagePath = `users/${uid}/avatar.jpg`;
          const photoRef = ref(storage, userImagePath);

          await deleteObject(photoRef)
            .then(() => {
              return true;
            })
            .catch((error) => {
              throw new Error(`${error.errorCode}: ${error.message}`);
            });

          return { data: null };
        } catch (err) {
          // console.log(
          //   `${apiName} (Error) Error deleting client's image: ${err}`
          // );
          throw new Error('Error deleting your photo');
        }
      },
    }),

    markNotificationAsRead: builder.mutation<null, string>({
      async queryFn(notificationId) {
        try {
          const uid = auth.currentUser?.uid;
          if (!uid) throw new Error('Current user UID cannot be undefined');
          if (!notificationId) throw new Error('Notification ID is required');

          const notificationRef = refDB(
            database,
            `notifications/${uid}/${notificationId}`
          );
          await update(notificationRef, { read: true });

          return { data: null };
        } catch (err) {
          // console.log(`${apiName} Error marking notification as read: ${err}`);
          throw new Error('Error marking notification as read');
        }
      },
    }),
  }),
});

export const {
  useGetCurrentUserQuery,
  useLazyGetCurrentUserQuery,
  useGetUserFromUIDQuery,
  useGetUserBlockStateQuery,
  useUpdateBlockStateMutation,
  useLazyGetUserFromUIDQuery,
  useGetFriendsForUserWithUIDQuery,
  useGetUserNotificationsQuery,
  useUpdateUserDataMutation,
  useUpdateUserLocationMutation,
  useUploadUserPhotoMutation,
  useDeleteUserPhotoMutation,
  useGetCurrentUserPrivacyStatesQuery,
  useGetCurrentUserLocationQuery,
  useUpdatePrivacySettingsMutation,
  useGetCurrentUserAccountStatesQuery,
  useUpdateUserAccountStatesMutation,
  useMarkNotificationAsReadMutation,
} = userAPI;
